import {
  MARKET_STRATEGY_FIELDS,
  EXECUTION_PLAN_FIELDS,
  GROWTH_STRATEGY_FIELDS,
} from "./ideaFormConfig";

export const formatSubmitterDisplayName = (submitter) => {
  const name = (submitter?.name || "").trim();
  if (name && name !== "Unknown") return name;
  const email = (submitter?.email || "").trim();
  if (email) return email;
  return "";
};

export const formatIdeaDate = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

export const getFileIcon = (type) => {
  const icons = {
    pdf: { bg: "bg-red-500", text: "PDF" },
    jpg: { bg: "bg-violet-500", text: "JPG" },
    jpeg: { bg: "bg-violet-500", text: "JPG" },
    mp4: { bg: "bg-blue-500", text: "MP4" },
    doc: { bg: "bg-sky-500", text: "DOC" },
    docx: { bg: "bg-sky-500", text: "DOC" },
    link: { bg: "bg-indigo-500", text: "URL" },
    default: { bg: "bg-slate-500", text: "FILE" },
  };
  return icons[type?.toLowerCase()] || icons.default;
};

export const formatFileSize = (size) => size || "—";

export const readIdeaUrlField = (source, snakeKey, camelKey) => {
  if (!source) return "";
  const candidates = [source[snakeKey], source[camelKey]];
  const nested = source.idea;
  if (nested && typeof nested === "object") {
    candidates.push(nested[snakeKey], nested[camelKey]);
  }
  for (const value of candidates) {
    const trimmed = String(value ?? "").trim();
    if (trimmed) return trimmed;
  }
  return "";
};

const inferFileTypeFromUrl = (url = "") => {
  const lower = String(url).toLowerCase();
  if (lower.includes(".pdf")) return "pdf";
  if (lower.includes(".docx")) return "docx";
  if (lower.includes(".doc")) return "doc";
  if (lower.includes(".mp4") || lower.includes("youtube") || lower.includes("vimeo")) return "mp4";
  if (lower.includes(".jpg") || lower.includes(".jpeg")) return "jpg";
  if (lower.includes(".png")) return "jpg";
  return "link";
};

const formatSectionKey = (key) =>
  String(key || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase())
    .trim();

const addFileEntry = (entries, seenUrls, file) => {
  const url = String(file?.url || file?.link || "").trim();
  if (!url || seenUrls.has(url)) return;
  seenUrls.add(url);
  entries.push({
    id: file.id || url,
    name: file.name || "Attachment",
    size: file.size || "Link",
    type: file.type || inferFileTypeFromUrl(url),
    fileTag: file.fileTag || "Document",
    uploadedOn: file.uploadedOn || "—",
    url,
    stage: file.stage || "",
  });
};

const addSectionFiles = (entries, seenUrls, section, stageLabel) => {
  if (!section || typeof section !== "object") return;
  Object.entries(section).forEach(([key, item]) => {
    const file = item?.file;
    if (!file?.url) return;
    addFileEntry(entries, seenUrls, {
      id: `${stageLabel}-${key}`,
      name: file.name && file.name !== "View Resource"
        ? file.name
        : `${stageLabel} · ${formatSectionKey(key)}`,
      size: file.size || "Link",
      type: file.type || inferFileTypeFromUrl(file.url),
      fileTag: stageLabel,
      uploadedOn: item.date || "—",
      url: file.url,
      stage: stageLabel,
    });
  });
};

const STAGE_URL_FIELD_GROUPS = [
  [],
  MARKET_STRATEGY_FIELDS,
  EXECUTION_PLAN_FIELDS,
  GROWTH_STRATEGY_FIELDS,
  [{ label: "Product video link", urlField: "product_video_link" }],
];

export const getEditableUrlFieldsForStage = (activeStage) => {
  const fields = [];
  for (let stageIndex = 1; stageIndex <= activeStage && stageIndex < STAGE_URL_FIELD_GROUPS.length; stageIndex += 1) {
    STAGE_URL_FIELD_GROUPS[stageIndex].forEach((field) => {
      if (field.urlField) fields.push(field);
    });
  }
  return fields;
};

const formatAttachmentDate = (dateString) => {
  if (!dateString) return "—";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

const getRecordUrl = (record, keys) => {
  for (const key of keys) {
    const value = String(record?.[key] || "").trim();
    if (value) return value;
  }
  return "";
};

const SPARK_ATTACHMENT_SOURCES = [
  { keys: ["ideaVideoLink", "idea_video_link"], name: "Idea Video Link", fileTag: "Video", type: "mp4" },
  { keys: ["conceptDiagram", "concept_diagram"], name: "Concept Diagram Link", fileTag: "Diagram", type: "link" },
  { keys: ["otherDocument", "other_document"], name: "Other Document Link", fileTag: "Document" },
  { keys: ["ideaImage", "idea_image"], name: "Idea Image", fileTag: "Image" },
  { keys: ["pitchVideoLink", "pitch_video_link"], name: "Pitch Video Link", fileTag: "Video", type: "mp4" },
];

const addSparkAttachmentsFromRecord = (entries, seenUrls, record) => {
  const submissionDate =
    record?.submissionDate || record?.submission_date || record?.created_at || "";
  const uploadedOn = formatAttachmentDate(submissionDate);

  SPARK_ATTACHMENT_SOURCES.forEach(({ keys, name, fileTag, type }) => {
    const url = getRecordUrl(record, keys);
    if (!url) return;
    addFileEntry(entries, seenUrls, {
      id: `spark-${keys[0]}`,
      name,
      size: "Link",
      type: type || inferFileTypeFromUrl(url),
      fileTag,
      uploadedOn,
      url,
      stage: "Spark",
    });
  });
};

export const getAttachmentSaveFieldKeys = (activeStage) => {
  const keys = [
    "idea_video_link",
    "concept_diagram",
    "other_document",
    "idea_image",
    "pitch_video_link",
  ];
  getEditableUrlFieldsForStage(activeStage).forEach(({ urlField }) => {
    if (urlField) keys.push(urlField);
  });
  return keys;
};

export const normalizeIdeaForAttachments = (record) => {
  if (!record) return null;

  const sparkFields = {
    ideaVideoLink: readIdeaUrlField(record, "idea_video_link", "ideaVideoLink"),
    conceptDiagram: readIdeaUrlField(record, "concept_diagram", "conceptDiagram"),
    otherDocument: readIdeaUrlField(record, "other_document", "otherDocument"),
    ideaImage: readIdeaUrlField(record, "idea_image", "ideaImage"),
    pitchVideoLink: readIdeaUrlField(record, "pitch_video_link", "pitchVideoLink"),
  };

  if (record.marketStrategy || record.executionPlan || record.growthStrategy) {
    return { ...record, ...sparkFields };
  }

  return {
    ...record,
    ...sparkFields,
    files: record.files || [],
    marketStrategy: {
      idealCustomerProfile: {
        file: record.idealCustomerProfileUrl ? { url: record.idealCustomerProfileUrl } : null,
      },
      valueProposition: {
        file: record.valuePropositionUrl ? { url: record.valuePropositionUrl } : null,
      },
      productMarketFit: {
        file: record.productMarketFitUrl ? { url: record.productMarketFitUrl } : null,
      },
      targetAddressableMarket: {
        file: record.targetAddressableMarketUrl ? { url: record.targetAddressableMarketUrl } : null,
      },
    },
    executionPlan: {
      mvp: { file: record.mvpUrl ? { url: record.mvpUrl } : null },
      marketValidation: {
        file: record.marketValidationUrl ? { url: record.marketValidationUrl } : null,
      },
      financialProjections: {
        file: record.financialProjectionsUrl ? { url: record.financialProjectionsUrl } : null,
      },
      microGrantProposal: {
        file: record.microGrantProposalUrl ? { url: record.microGrantProposalUrl } : null,
      },
    },
    growthStrategy: {
      enhanceBusinessModel: {
        file: record.enhanceBusinessModelUrl ? { url: record.enhanceBusinessModelUrl } : null,
      },
      productReadiness: {
        file: record.productReadinessUrl ? { url: record.productReadinessUrl } : null,
      },
      customerProfile: {
        file: record.customerProfileUrl ? { url: record.customerProfileUrl } : null,
      },
      gtmStrategy: { file: record.gtmStrategyUrl ? { url: record.gtmStrategyUrl } : null },
      productMarketFit: {
        file: record.productMarketFitUrl ? { url: record.productMarketFitUrl } : null,
      },
      targetAddressableMarket: {
        file: record.targetAddressableMarketUrl ? { url: record.targetAddressableMarketUrl } : null,
      },
      customerFeedback: {
        file: record.customerFeedbackUrl ? { url: record.customerFeedbackUrl } : null,
      },
    },
    pitchPreparation: record.pitchPreparation,
  };
};

export const mergeAttachmentFormValues = (engagement, formValues = {}) => {
  if (!engagement) return engagement;
  const pick = (formKey, engagementKey) =>
    String(formValues[formKey] ?? "").trim() || engagement[engagementKey] || "";

  return {
    ...engagement,
    ideaVideoLink: pick("idea_video_link", "ideaVideoLink"),
    conceptDiagram: pick("concept_diagram", "conceptDiagram"),
    otherDocument: pick("other_document", "otherDocument"),
    ideaImage: pick("idea_image", "ideaImage"),
    pitchVideoLink: pick("pitch_video_link", "pitchVideoLink"),
  };
};

export const collectAllIdeaAttachments = (record) => {
  const engagement = normalizeIdeaForAttachments(record);
  if (!engagement) return [];

  const entries = [];
  const seenUrls = new Set();

  (engagement.files || []).forEach((file) =>
    addFileEntry(entries, seenUrls, {
      ...file,
      stage: file.stage || "Spark",
    })
  );

  addSparkAttachmentsFromRecord(entries, seenUrls, engagement);

  addSectionFiles(entries, seenUrls, engagement.marketStrategy, "Shape");
  addSectionFiles(entries, seenUrls, engagement.executionPlan, "Structure");
  addSectionFiles(entries, seenUrls, engagement.growthStrategy, "Scale");

  const pitchAdditional = engagement.pitchPreparation?.additional;
  if (pitchAdditional?.productVideoLink) {
    addFileEntry(entries, seenUrls, {
      id: "shine-product-video",
      name: "Product Video",
      size: "Link",
      type: inferFileTypeFromUrl(pitchAdditional.productVideoLink),
      fileTag: "Shine",
      uploadedOn: "—",
      url: pitchAdditional.productVideoLink,
      stage: "Shine",
    });
  }

  const pitchDeck = pitchAdditional?.pitchDeckUpload;
  const pitchDeckUrl =
    typeof pitchDeck === "string"
      ? pitchDeck
      : pitchDeck?.url || pitchDeck?.file?.url || "";
  if (pitchDeckUrl) {
    addFileEntry(entries, seenUrls, {
      id: "shine-pitch-deck",
      name: "Pitch Deck",
      size: "Link",
      type: inferFileTypeFromUrl(pitchDeckUrl),
      fileTag: "Shine",
      uploadedOn: "—",
      url: pitchDeckUrl,
      stage: "Shine",
    });
  }

  return entries;
};

const STAGE_SPECIFIC_TABS = {
  0: [],
  1: [{ id: "market-strategy", label: "Market Strategy" }],
  2: [{ id: "execution-plan", label: "Execution Plan" }],
  3: [{ id: "growth-strategy", label: "Growth Strategy" }],
  4: [{ id: "pitch-preparation", label: "Pitch Preparation" }],
};

export const getEngagementStageTabs = (activeStage) => {
  const reviewLabel = activeStage === 0 || activeStage === 1 ? "Review & Scores" : "Review & Score";
  return [
    { id: "overview", label: "Idea Overview" },
    ...(STAGE_SPECIFIC_TABS[activeStage] || []),
    { id: "attachments", label: "Attachments" },
    { id: "collaboration", label: "Collaboration" },
    { id: "review", label: reviewLabel },
  ];
};
